import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ChevronDown, ChevronUp, ExternalLink, FileText, House, LogOut, Settings } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, Redirect, useLocation } from "wouter";
import type { ToolMeta } from "../../shared/tools";
import { type ComingSoonTool, comingSoonTools, webTools } from "../../tools/web";
import { api, inFrame, phoneView, signedInUrl } from "../lib/api";
import { CompanyContext } from "../lib/company";
import type { CompanySummary, Me } from "../lib/queries";
import type { ConnectionState } from "../lib/realtime";
import { themeLocked } from "../lib/theme";
import { ConnectionBanner } from "./ConnectionBanner";
import { Icon } from "./Icon";
import { SyncLine, SyncStatus } from "./SyncStatus";
import { ThemePicker } from "./ThemePicker";
import { useWithWindows } from "./ToolWindows";
import { Popover } from "./ui";

/*
 * The frame around every page, made to look like part of ServiceM8.
 *  - Wide screens (in a browser, and inside ServiceM8, whose own bar takes the top of the screen):
 *    a sidebar, so the page keeps its height. Home and the tools at the top, each tool's sections
 *    under it; at the bottom, Settings (apart from the tools: it's used now and then), then you,
 *    your business and the live sync status (the account menu: theme, except inside ServiceM8 on
 *    the web; Settings; sign out).
 *  - Narrow screens and phones: tabs across the top (in a browser, under a top bar with the
 *    business, sync status and account menu). Tools show their sections as tabs of their own then.
 */
export function Shell(props: Readonly<{ me: Me; companyId: string; realtime: ConnectionState; children: ReactNode }>) {
    const { me, companyId, realtime } = props;
    const company = me.companies.find((c) => c.id === companyId);
    // Every link between pages keeps the open windows open (ToolWindows.tsx), as the sidebar's do.
    const withWindows = useWithWindows();
    if (!company) return <Redirect to="~/" replace />;

    const tools = company.tools.map((id) => webTools[id]?.meta).filter((m): m is ToolMeta => m !== undefined);
    const showTopBar = !inFrame && !phoneView;
    // Settings: Appearance for everyone, the rest for managers (pages/settings). Not on phones.
    const settings = !phoneView;

    return (
        <CompanyContext value={company}>
            <div className="flex h-full">
                {phoneView ? null : (
                    <aside className="hidden w-56 shrink-0 flex-col border-r border-line bg-surface md:flex" aria-label="Sections">
                        <Link href={withWindows("/")} className="block border-b border-line px-4 py-3 no-underline">
                            <span className="block font-bold text-ink">Outback Workbench</span>
                            <span className="block truncate text-xs text-muted">{company.name}</span>
                        </Link>
                        <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
                            <NavLink href="/" icon={<House className="size-4" />} label="Home" />
                            {tools.map((m) => (
                                <ToolNav key={m.id} meta={m} />
                            ))}
                            {comingSoonTools.map((t) => (
                                <ComingSoonNav key={t.id} tool={t} />
                            ))}
                        </nav>
                        {/* Settings: used now and then, so at the foot rather than among the tools. Inside ServiceM8, with a way out to more room. */}
                        {settings || inFrame ? (
                            <div className="space-y-0.5 border-t border-line px-2 py-2">
                                {inFrame ? <NewTabLink /> : null}
                                {settings ? <NavLink href="/settings" icon={<Settings className="size-4" />} label="Settings" /> : null}
                            </div>
                        ) : null}
                        <SidebarAccount me={me} company={company} realtime={realtime} canSettings={settings} />
                    </aside>
                )}
                <div className="flex min-w-0 flex-1 flex-col">
                    <div className={phoneView ? undefined : "md:hidden"}>
                        {showTopBar && (
                            <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line bg-surface px-4">
                                <Link href={withWindows("/")} className="font-bold text-ink no-underline">
                                    Outback Workbench
                                </Link>
                                <span className="hidden truncate text-sm text-muted sm:inline">{company.name}</span>
                                <div className="ml-auto flex items-center gap-3">
                                    <SyncStatus company={company} realtime={realtime} />
                                    <AccountMenu me={me} companyName={company.name} />
                                </div>
                            </header>
                        )}
                        <nav className="tabs shrink-0" aria-label="Sections">
                            <Tab href="/" icon={<House className="size-4" />} label="Home" />
                            {tools.map((m) => (
                                <Tab key={m.id} href={`/t/${m.id}`} icon={<Icon name={m.icon} className="size-4" />} label={m.name} />
                            ))}
                            {settings && <Tab href="/settings" icon={<Settings className="size-4" />} label="Settings" />}
                            {showTopBar ? null : (
                                <div className="ml-auto flex items-center pl-3">
                                    <SyncStatus company={company} realtime={realtime} />
                                </div>
                            )}
                        </nav>
                    </div>
                    <ConnectionBanner company={company} />
                    {/* The scrollbar's space always kept, so a page doesn't shift sideways as it gets long enough to scroll. */}
                    <main className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">{props.children}</main>
                </div>
            </div>
        </CompanyContext>
    );
}

const useActive = (href: string) => {
    const [location] = useLocation();
    return href === "/" ? location === "/" : location.startsWith(href);
};

/** A sidebar link: green bar and bold when it's where you are. */
/**
 * Inside ServiceM8: this page in its own browser tab, for the whole screen instead of ServiceM8's
 * window, still signed in (lib/api.ts signedInUrl, worked out as it's clicked: the page may have changed).
 */
function NewTabLink() {
    return (
        <a
            href={signedInUrl()}
            target="_blank"
            rel="noreferrer"
            title="Opens this page in its own browser tab, with the whole screen to work in. You stay signed in."
            onClick={(e) => {
                e.currentTarget.href = signedInUrl();
            }}
            className="flex items-center gap-3 rounded-md border-l-[3px] border-transparent px-3 py-2 text-sm text-ink no-underline transition hover:bg-surface-2"
        >
            <span className="text-muted">
                <ExternalLink className="size-4" />
            </span>
            Open in a new tab
        </a>
    );
}

function NavLink({ href, icon, label }: Readonly<{ href: string; icon: ReactNode; label: string }>) {
    const withWindows = useWithWindows();
    const active = useActive(href);
    return (
        <Link
            href={withWindows(href)}
            aria-current={active ? "page" : undefined}
            className={clsx(
                "flex items-center gap-3 rounded-md border-l-[3px] px-3 py-2 text-sm text-ink no-underline transition",
                active ? "border-brand-500 bg-surface-2 font-semibold" : "border-transparent hover:bg-surface-2",
            )}
        >
            <span className={active ? "text-brand-ink" : "text-muted"}>{icon}</span>
            {label}
        </Link>
    );
}

/** A tool on the way, in the sidebar: like a tool's link, but quiet and not a link, with "Coming soon" under its name (the sidebar's too narrow for both on one line). */
function ComingSoonNav({ tool }: Readonly<{ tool: ComingSoonTool }>) {
    return (
        <div className="flex cursor-default items-start gap-3 rounded-md border-l-[3px] border-transparent px-3 py-2 text-sm text-muted" title={tool.description} aria-disabled="true">
            <Icon name={tool.icon} className="mt-0.5 size-4 shrink-0" />
            <span className="min-w-0">
                <span className="block truncate">{tool.name}</span>
                <span className="block text-[11px] leading-tight">Coming soon</span>
            </span>
        </div>
    );
}

/** A tool in the sidebar, with its sections (if any) always listed underneath, for one click to each. */
function ToolNav({ meta }: Readonly<{ meta: ToolMeta }>) {
    const href = `/t/${meta.id}`;
    return (
        <div>
            <NavLink href={href} icon={<Icon name={meta.icon} className="size-4" />} label={meta.name} />
            {meta.sections ? (
                <SubNav href={href} sections={meta.sections.map((s) => ({ id: s.id, name: s.name, icon: <Icon name={s.icon} className="size-3.5" /> }))} />
            ) : null}
        </div>
    );
}

/** Sections listed under a sidebar link, indented. The first is what the plain address shows. */
function SubNav({ href, sections }: Readonly<{ href: string; sections: { id: string; name: string; icon: ReactNode }[] }>) {
    const withWindows = useWithWindows();
    const [location] = useLocation();
    return (
        <div className="mt-0.5 mb-1 ml-5 space-y-0.5 border-l border-line pl-2">
            {sections.map((s, i) => {
                const sectionHref = `${href}/${s.id}`;
                const active = location.startsWith(sectionHref) || (i === 0 && location.replace(/\/$/, "") === href);
                return (
                    <Link
                        key={s.id}
                        href={withWindows(sectionHref)}
                        aria-current={active ? "page" : undefined}
                        className={clsx(
                            "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-ink no-underline transition [&_svg]:size-3.5",
                            active ? "bg-surface-2 font-semibold" : "hover:bg-surface-2",
                        )}
                    >
                        <span className={active ? "text-brand-ink" : "text-muted"}>{s.icon}</span>
                        {s.name}
                    </Link>
                );
            })}
        </div>
    );
}

function Tab({ href, icon, label }: Readonly<{ href: string; icon: ReactNode; label: string }>) {
    const withWindows = useWithWindows();
    const active = useActive(href);
    return (
        <Link href={withWindows(href)} className="tab" aria-current={active ? "page" : undefined}>
            {icon}
            {label}
        </Link>
    );
}

function useSignOut() {
    const qc = useQueryClient();
    return async () => {
        await api("/auth/logout", { method: "POST" });
        qc.clear();
        location.href = "/login";
    };
}

/**
 * The account menu: who you are and where, theme (not inside ServiceM8 on the web, where the app
 * stays light: Settings > Appearance explains), Settings (not on phones: appearance, and for
 * managers the ServiceM8 connection and sync, access, people), the privacy policy and terms (in a
 * new tab) and sign out. Settings lives here, not with the tools.
 */
function AccountDetails(props: Readonly<{ me: Me; companyName: string; canSettings: boolean; onDone: () => void; onSignOut: () => void }>) {
    const { me } = props;
    const withWindows = useWithWindows();
    return (
        <div className="space-y-2 p-2 text-ink">
            <div className="min-w-0 px-1">
                <div className="truncate text-sm font-semibold">{me.user.name ?? me.user.email}</div>
                {me.user.name ? <div className="truncate text-xs text-muted">{me.user.email}</div> : null}
                <div className="truncate text-xs text-muted">{props.companyName}</div>
            </div>
            {/* Not inside ServiceM8 on the web, where the app stays light (lib/theme.ts): Settings > Appearance explains, with a link out. */}
            {themeLocked ? null : (
                <div className="space-y-1 border-t border-line pt-2">
                    <div className="px-1 text-xs font-medium text-muted">Theme</div>
                    <ThemePicker compact />
                </div>
            )}
            <div className="space-y-0.5 border-t border-line pt-2">
                {props.canSettings ? (
                    <Link href={withWindows("/settings")} onClick={props.onDone} className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-ink no-underline hover:bg-surface-2">
                        <Settings className="size-4 text-muted" /> Settings
                    </Link>
                ) : null}
                <a href="/privacy" target="_blank" rel="noreferrer" onClick={props.onDone} className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-ink no-underline hover:bg-surface-2">
                    <FileText className="size-4 text-muted" /> Privacy and terms
                </a>
                <button type="button" className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-danger hover:bg-surface-2" onClick={props.onSignOut}>
                    <LogOut className="size-4" /> Sign out
                </button>
            </div>
        </div>
    );
}

/** Narrow screens in a browser: your name in the top bar, opening the account menu. */
function AccountMenu({ me, companyName }: Readonly<{ me: Me; companyName: string }>) {
    const signOut = useSignOut();
    return (
        <Popover
            label="Account"
            width="w-64"
            buttonClassName="flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-sm text-ink hover:bg-surface-2"
            button={
                <>
                    {me.user.name ?? me.user.email} <ChevronDown className="size-4" />
                </>
            }
        >
            {(close) => <AccountDetails me={me} companyName={companyName} canSettings={false} onDone={close} onSignOut={() => void signOut()} />}
        </Popover>
    );
}

/**
 * The sidebar's foot, like ServiceM8's account corner: you, your business and the live sync
 * status; click for the account menu (opening upwards), where Settings and sign out are.
 */
function SidebarAccount(props: Readonly<{ me: Me; company: CompanySummary; realtime: ConnectionState; canSettings: boolean }>) {
    const { me, company } = props;
    const signOut = useSignOut();
    const [open, setOpen] = useState(false);
    const box = useRef<HTMLDivElement>(null);
    // Closes on a click elsewhere or Escape.
    useEffect(() => {
        if (!open) return;
        const onDown = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        document.addEventListener("pointerdown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("pointerdown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    return (
        <div ref={box} className="relative border-t border-line p-2">
            {open ? (
                <div className="card absolute inset-x-2 bottom-full mb-2">
                    <AccountDetails me={me} companyName={company.name} canSettings={props.canSettings} onDone={() => setOpen(false)} onSignOut={() => void signOut()} />
                </div>
            ) : null}
            <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                className={clsx("flex w-full cursor-pointer items-center gap-2 rounded-md p-2 text-left hover:bg-surface-2", open && "bg-surface-2")}
                onClick={() => setOpen((v) => !v)}
            >
                <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-ink">{me.user.name ?? me.user.email}</span>
                    <span className="block truncate text-xs text-muted">{company.name}</span>
                    <SyncLine company={company} realtime={props.realtime} />
                </span>
                <ChevronUp className={clsx("size-4 shrink-0 text-muted transition", !open && "rotate-180")} />
            </button>
        </div>
    );
}
